import { useQuery } from '@apollo/client';
import { useFeatureFlag } from '@newfront-insurance/admin-ui';
import { isEmpty } from '@newfront-insurance/core';
import { useMemo } from 'react';

import { useSkillsSafe } from '../components/chat-v2/contexts/skills-context';
import { buildPromptCommands } from '../lib/slash-command-constants';
import { GET_SKILLS, GET_SLASH_COMMANDS } from '../queries/chats';

interface UsePromptCommandsOptions {
  accountUuid?: string;
  userUuid?: string;
}

export function usePromptCommands({ accountUuid, userUuid }: UsePromptCommandsOptions) {
  const isNovaSkillsEnabled = useFeatureFlag('nova-skills', false);
  const skillsContext = useSkillsSafe();
  const slashCommandsQuery = useQuery(GET_SLASH_COMMANDS, {
    variables: {
      accountUuid,
      userUuid: userUuid ?? '',
    },
    skip: isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const skillsQuery = useQuery(GET_SKILLS, {
    skip: !isNovaSkillsEnabled || Boolean(skillsContext) || isEmpty(userUuid),
    fetchPolicy: 'cache-and-network',
  });

  const slashCommands = useMemo(
    () => slashCommandsQuery.data?.slashCommands ?? [],
    [slashCommandsQuery.data?.slashCommands],
  );
  const skills = useMemo(
    () => (isNovaSkillsEnabled ? (skillsContext?.skills ?? skillsQuery.data?.skills ?? []) : []),
    [isNovaSkillsEnabled, skillsContext, skillsQuery.data?.skills],
  );
  const commands = useMemo(() => buildPromptCommands(slashCommands, skills), [skills, slashCommands]);

  return {
    commands,
    slashCommands,
    skills,
    isLoading:
      slashCommandsQuery.loading || (isNovaSkillsEnabled ? skillsContext?.isLoading || skillsQuery.loading : false),
    error: slashCommandsQuery.error ?? (isNovaSkillsEnabled ? (skillsContext?.error ?? skillsQuery.error) : undefined),
  };
}
